"use client";

import { z } from "zod";
import {
  useForm,
  SubmitHandler,
  FieldError,
  UseFormRegisterReturn,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "@/src/components/ui/Button";
import { sendContactMessage } from "@/src/app/actions/contact";
import { useTranslations, useLocale } from "next-intl";
import Modal from "@/src/components/ui/Modal";
import { CheckCircle, XCircle } from "lucide-react";
import React, { useState, useRef, useEffect } from "react";

import "react-phone-number-input/style.css";
import PhoneInputWithCountry from "react-phone-number-input/react-hook-form";
import { getCountryCallingCode } from "react-phone-number-input";
import arLabels from "react-phone-number-input/locale/ar.json";
import enLabels from "react-phone-number-input/locale/en.json";

const PhoneErrorContext = React.createContext(false);

export const FormInput = ({
  register,
  placeholder,
  title,
  textarea = false,
  error,
  ...props
}: {
  register: UseFormRegisterReturn;
  placeholder: string;
  title?: string;
  textarea?: boolean;
  error: FieldError | undefined;
} & React.HTMLAttributes<HTMLInputElement | HTMLTextAreaElement>) => {
  const InputType = textarea ? "textarea" : "input";

  return (
    <div className="mb-3 md:mb-4">
      {title && (
        <span className={`block button-text mb-2 text-text-secondary`}>
          {title}
        </span>
      )}
      <InputType
        className={`focus:outline-0 w-full outline-1 outline-text-disabled rounded-lg py-3 px-4 placeholder:text-text-disabled focus:outline-primary focus:outline-2 ${
          textarea && "resize-none h-[120px]"
        } ${error && "outline-accent!"}`}
        {...register}
        placeholder={placeholder}
        {...props}
      />
      {error && <p className="text-accent small-text">{error.message}</p>}
    </div>
  );
};

const PhoneNumberInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>((props, ref) => {
  const hasError = React.useContext(PhoneErrorContext);
  return (
    <input
      ref={ref}
      {...props}
      className={`focus:outline-0 w-full outline-1 outline-text-disabled rounded-lg py-3 px-4 placeholder:text-text-disabled focus:outline-primary focus:outline-2${
        hasError ? " outline-accent!" : ""
      }`}
    />
  );
});
PhoneNumberInput.displayName = "PhoneNumberInput";

interface PhoneCountrySelectProps {
  value?: string;
  onChange: (value?: string) => void;
  options: Array<{ value?: string; label: string; divider?: boolean }>;
  iconComponent?: React.ComponentType<{ country: string; title: string }>;
  unicodeFlags?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  tabIndex?: number;
  className?: string;
  arrow?: React.ReactNode;
  "aria-label"?: string;
}

const PhoneCountrySelect = ({
  value,
  onChange,
  options,
  iconComponent: FlagIcon,
}: PhoneCountrySelectProps) => {
  const hasError = React.useContext(PhoneErrorContext);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchBufferRef = useRef("");
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectOptions = options
    .filter(
      (opt): opt is { value: string; label: string } =>
        opt.value !== undefined && !opt.divider && opt.value !== "IL",
    )
    .map((opt) => {
      return { value: opt.value, label: opt.label };
    });

  const selectedOption = selectOptions.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`cursor-pointer bg-white outline-1 rounded-lg py-3 px-4 text-primary flex items-center gap-1.5 h-[50px] ${
          hasError ? "outline-accent!" : "outline-text-disabled"
        }`}
      >
        {FlagIcon && value && (
          <FlagIcon
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            country={value as any}
            title={selectedOption?.label ?? ""}
          />
        )}
        <span className="text-sm text-gray-700">
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {value ? `+${getCountryCallingCode(value as any)}` : "__"}
        </span>

        <svg
          width="10"
          height="6"
          viewBox="0 0 10 6"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={`transform transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        >
          <path
            d="M1 1L5 5L9 1"
            stroke="#9CA3AF"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {isOpen && (
        <div className="absolute top-full rtl:right-0 ltr:left-0 mt-1 bg-white rounded-lg shadow-lg z-150 overflow-hidden border border-gray-100 w-max">
          <div ref={listRef} className="max-h-50 overflow-y-auto">
            {selectOptions.map((option) => (
              <div
                key={option.value}
                data-value={option.value}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`px-4 py-2 flex items-center gap-4 text-gray-700 hover:bg-[#E6E6E6] cursor-pointer transition-colors duration-150 ${
                  value === option.value ? "bg-[#E6E6E6]" : ""
                }`}
                title={option.label}
              >
                {FlagIcon && (
                  <FlagIcon
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    country={option.value as any}
                    title={option.label}
                  />
                )}
                <span className="truncate text-sm md:text-base">
                  {option.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

type PropsType = {
  className?: string;
};

const ContactForm = ({ className }: PropsType) => {
  const t = useTranslations("Contact.form");
  const tResult = useTranslations("ContactResult");
  const tButtons = useTranslations("Buttons");
  const locale = useLocale();
  const phoneLabels = locale === "ar" ? arLabels : enLabels;
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const [phoneNumberValue, setPhoneNumberValue] = useState<string | undefined>(
    "",
  );

  const schema = z.object({
    name: z.string().min(1, t("errors.name")),
    email: z.email(t("errors.email")),
    phone: z
      .string()
      .min(4, t("errors.phoneRequired"))
      .regex(/^\+?[0-9]{10,15}$/, t("errors.phoneInvalid")),
    message: z.string().min(1, t("errors.message")),
  });

  type FormFields = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: (values, context, options) =>
      zodResolver(schema)(
        { ...values, phone: values.phone ?? "" },
        context,
        options,
      ),
  });

  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      const response = await sendContactMessage(data);
      if (response && !response.error) {
        setShowSuccessModal(true);
        reset();
      } else {
        setShowErrorModal(true);
      }
    } catch (error) {
      console.log(error);
      setShowErrorModal(true);
    }
  };

  return (
    <div className={className}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormInput
          register={register("name", { required: true })}
          placeholder={t("namePlaceholder")}
          title={t("nameLabel")}
          error={errors.name}
        />

        <FormInput
          register={register("email", { required: true })}
          placeholder={t("emailPlaceholder")}
          title={t("emailLabel")}
          error={errors.email}
        />

        <div className="mb-3 md:mb-4">
          <span className="block button-text mb-2 text-text-secondary">
            {t("phoneLabel")}
          </span>
          <PhoneErrorContext.Provider value={!!errors.phone}>
            <PhoneInputWithCountry
              className="flex flex-row gap-4"
              name="phone"
              control={control}
              value={phoneNumberValue}
              onChange={setPhoneNumberValue}
              inputComponent={PhoneNumberInput}
              defaultCountry="EG"
              countrySelectComponent={PhoneCountrySelect}
              labels={phoneLabels}
            />
          </PhoneErrorContext.Provider>
          {errors.phone && (
            <p className="text-accent small-text">{errors.phone.message}</p>
          )}
        </div>

        <FormInput
          register={register("message", { required: true })}
          placeholder={t("messagePlaceholder")}
          title={t("messageLabel")}
          textarea
          error={errors.message}
        />

        <Button wide disabled={isSubmitting} className="gap-4">
          {tButtons("send")}
        </Button>
      </form>

      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
      >
        <div className="flex flex-col items-center justify-center text-center p-8 md:p-10">
          <div className="mb-6 text-green-600">
            <CheckCircle size={80} strokeWidth={1.5} />
          </div>
          <h3 className="text-2xl font-bold mb-3 text-text-primary">
            {tResult("success.title")}
          </h3>
          <p className="text-text-secondary mb-8 leading-relaxed">
            {tResult("success.description")}
          </p>
          <Button
            wide
            onClick={() => setShowSuccessModal(false)}
            className="min-w-[200px]"
          >
            {tResult("success.button")}
          </Button>
        </div>
      </Modal>

      <Modal isOpen={showErrorModal} onClose={() => setShowErrorModal(false)}>
        <div className="flex flex-col items-center justify-center text-center p-8 md:p-10">
          <div className="mb-6 text-red-600">
            <XCircle size={80} strokeWidth={1.5} />
          </div>
          <h3 className="text-2xl font-bold mb-3 text-text-primary">
            {tResult("failed.title")}
          </h3>
          <p className="text-text-secondary mb-8 leading-relaxed">
            {tResult("failed.description")}
          </p>
          <Button
            wide
            onClick={() => setShowErrorModal(false)}
            className="min-w-50"
          >
            {tResult("failed.button")}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default ContactForm;
